import { cn } from '@/lib/utils'
import { getSeenEventRelayUrls } from '@/services/client.service'
import { Event } from '@nostr/tools/wasm'
import { pool } from '@nostr/gadgets/global'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Content from '../Content'
import Username from '../Username'
import { FormattedTimestamp } from '../FormattedTimestamp'
import Nip29EventHeader from './Nip29EventHeader'

const CONTEXT_MINUTES = 30
const CONTEXT_MESSAGES = 5

export default function ChatMessage({
  event,
  className,
  clamp,
  showFull = false
}: {
  event: Event
  className?: string
  clamp?: boolean
  showFull?: boolean
}) {
  const { t } = useTranslation()
  const [context, setContext] = useState<Event[] | null>(null)

  const channel = event.tags.find(([name]) => name === 'h')?.[1]
  const relay = getSeenEventRelayUrls(event.id, event)[0]

  useEffect(() => {
    if (!showFull || !channel || !relay) {
      setContext(null)
      return
    }

    let cancelled = false
    setContext(null)

    pool
      .querySync([relay], {
        kinds: [kinds.ChatMessage],
        '#h': [channel],
        since: event.created_at - CONTEXT_MINUTES * 60,
        until: event.created_at + CONTEXT_MINUTES * 60
      })
      .then((events) => {
        if (cancelled) return

        const ordered = [...events]
          .filter((candidate) => candidate.kind === kinds.ChatMessage)
          .sort((a, b) => a.created_at - b.created_at || a.id.localeCompare(b.id))
        const focusedIndex = ordered.findIndex((candidate) => candidate.id === event.id)
        if (focusedIndex === -1) {
          ordered.push(event)
          ordered.sort((a, b) => a.created_at - b.created_at || a.id.localeCompare(b.id))
        }
        const index = ordered.findIndex((candidate) => candidate.id === event.id)
        setContext(
          ordered.slice(
            Math.max(0, index - CONTEXT_MESSAGES),
            Math.min(ordered.length, index + CONTEXT_MESSAGES + 1)
          )
        )
      })
      .catch(() => {
        if (!cancelled) setContext(null)
      })

    return () => {
      cancelled = true
    }
  }, [channel, event, relay, showFull])

  if (context) {
    return (
      <div className={cn('space-y-2', className)}>
        <Nip29EventHeader event={event} label={t('Chat Message')} />
        <div className="space-y-1 rounded-lg border bg-muted/20 p-2">
          {context.map((message) => (
            <div
              key={message.id}
              className={cn(
                'rounded-md px-3 py-2',
                message.id === event.id && 'bg-primary/10 ring-1 ring-primary/30'
              )}
            >
              <div className="mb-1 flex items-baseline gap-2 text-xs text-muted-foreground">
                <Username user={message.pubkey} className="font-semibold text-foreground" />
                <FormattedTimestamp timestamp={message.created_at} absolute />
              </div>
              <Content
                event={message}
                clamp={message.id === event.id ? clamp : false}
                compactReferences
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Nip29EventHeader event={event} label={t('Chat Message')} />
      <Content event={event} clamp={clamp} />
    </div>
  )
}
